import { useEffect, useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useVerifyEmail } from '../hooks/useAuth';

const StyledCard = styled(Card)(() => ({
  maxWidth: '100%',
  width: '95%',
  '@media (min-width: 600px)': {
    width: '80%',
  },
  '@media (min-width: 900px)': {
    width: '70%',
  },
  '@media (min-width: 1200px)': {
    width: '60%',
  },
  '@media (min-width: 1536px)': {
    width: '50%',
  },
  margin: '0 auto',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  borderRadius: 16,
}));

const GradientBackground = styled(Box)(() => ({
  minHeight: '100vh',
  background: `linear-gradient(135deg, 
    #8b9cff15 0%, 
    #ff6b9d10 50%,
    #ffffff 100%)`,
}));

function EmailVerification() {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const hasAttemptedVerification = useRef(false);

  const verifyEmail = useVerifyEmail();

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    // console.log('🔍 EmailVerification useEffect - tokenFromUrl:', tokenFromUrl, 'hasAttempted:', hasAttemptedVerification.current);
    
    if (tokenFromUrl && !hasAttemptedVerification.current) {
      // console.log('✅ Starting email verification...');
      hasAttemptedVerification.current = true;
      handleVerification(tokenFromUrl);
    } else if (!tokenFromUrl) {
      // console.log('❌ No verification token provided');
      setStatus('error');
      setMessage(t('verifyEmail.noToken'));
    } else {
      // console.log('⏭️ Verification already attempted, skipping...');
    }
  }, [searchParams]);

  const handleVerification = async (verificationToken: string) => {
    // console.log('🔄 handleVerification called with token:', verificationToken.substring(0, 10) + '...');
    try {
      await verifyEmail.mutateAsync(verificationToken);
      // console.log('✅ Email verification successful');
      setStatus('success');
      setMessage(t('verifyEmail.verifiedDesc'));
    } catch (error) {
      // console.log('❌ Email verification failed:', error);
      setStatus('error');
      setMessage(error instanceof Error ? error.message : t('verifyEmail.failed'));
    }
  };

  const handleResendVerification = () => {
    // This would need the user's email, which we don't have in this component
    // In a real app, you might want to redirect to a page where they can enter their email
    window.location.href = '/login';
  };

  return (
    <GradientBackground>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <StyledCard>
          {/* Gradient Header Section */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
              color: 'white',
              textAlign: 'center',
              py: 3,
              px: 4,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '20px',
                background: 'linear-gradient(to bottom, rgba(102, 126, 234, 0.1), transparent)',
              }
            }}
          >
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              {t('verifyEmail.title')}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, fontSize: '1.1rem' }}>
              {t('verifyEmail.subtitle')}
            </Typography>
          </Box>
          
          {/* Content Section */}
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            {status === 'loading' && (
              <Box>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('verifyEmail.verifying')}
                </Typography>
                <Typography color="text.secondary">
                  {t('verifyEmail.verifyingDesc')}
                </Typography>
              </Box>
            )}

            {status === 'success' && (
              <Box>
                <Typography variant="h5" color="success.main" gutterBottom>
                  ✓ {t('verifyEmail.verified')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {message}
                </Typography>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{
                    background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #7a8bff 0%, #ff5a8d 100%)',
                    },
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                  }}
                >
                  {t('verifyEmail.continueToLogin')}
                </Button>
              </Box>
            )}

            {status === 'error' && (
              <Box>
                <Typography variant="h5" color="error.main" gutterBottom>
                  {t('verifyEmail.failed')}
                </Typography>
                <Alert severity="error" sx={{ mb: 3 }}>
                  {message}
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('verifyEmail.failedDesc')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('verifyEmail.needHelp')}{' '}
                  <a href="mailto:support@bizcontent.ai" style={{ color: '#8b9cff', textDecoration: 'none' }}>
                    support@bizcontent.ai
                  </a>
                </Typography>
                <Button
                  onClick={handleResendVerification}
                  variant="outlined"
                  size="large"
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  {t('verifyEmail.requestNew')}
                </Button>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="text"
                  size="large"
                  fullWidth
                >
                  {t('verifyEmail.backToLogin')}
                </Button>
              </Box>
            )}
          </CardContent>
        </StyledCard>
      </Container>
    </GradientBackground>
  );
}

export default EmailVerification;

